
<template>
  <div class="page" :class="{ light: theme === 'light' }" @click="dlMenuKey = null">
    <header class="nav">
      <span class="nav-brand">DANCELAB</span>
      <div class="nav-links">
        <router-link :to="{ name: 'index' }" class="nav-link" :class="{ 'is-active': $route.name === 'index' }">{{ lang === 'zh' ? '藏族舞者' : 'DANCER' }}</router-link>
        <span class="nav-sep">|</span>
        <router-link :to="{ name: 'home' }" class="nav-link" :class="{ 'is-active': $route.name === 'home' }">{{ lang === 'zh' ? '骨骼' : 'SKELETON' }}</router-link>
        <span class="nav-sep">|</span>
        <router-link :to="{ name: 'about' }" class="nav-link" :class="{ 'is-active': $route.name === 'about' }">{{ lang === 'zh' ? '项目介绍' : 'ABOUT' }}</router-link>
      </div>
      <div class="nav-controls">
        <div class="ctrl-lang" @click="toggleLang">
          <span :class="{ 'ctrl-active': lang === 'zh' }">ZH</span>
          <span class="ctrl-dot">·</span>
          <span :class="{ 'ctrl-active': lang === 'en' }">EN</span>
        </div>
        <div class="ctrl-theme" @click="toggleTheme">{{ theme === 'dark' ? '○' : '●' }}</div>
      </div>
    </header>

    <div class="content">
      <!-- LEFT: categorised animation list -->
      <div class="list-panel">
        <div class="dance-folder" @click="folderOpen = !folderOpen">
          <span class="folder-slash">/</span>
          <span class="folder-name">{{ lang === 'zh' ? '西藏藏族男子热巴舞' : 'TIBETAN MALE REBA DANCE' }}</span>
          <span class="folder-toggle">{{ folderOpen ? '▾' : '▸' }}</span>
        </div>

        <div class="cat-section" v-show="folderOpen" v-for="(group, gi) in groupedAnimations" :key="gi">
          <div class="cat-header">
            <span class="cat-roman">{{ group.roman }}</span>
            <span class="cat-label">{{ lang === 'zh' ? group.titleZh : group.titleEn }}</span>
          </div>
          <ul class="anim-grid">
            <li
              v-for="item in group.items"
              :key="item.index"
              class="anim-card"
              :class="{ selected: selectedKey === item.index }"
              @click="selectAnim(item, item.index)"
            >
              <div class="anim-thumb"><img :src="item.cover" :alt="item.titleZh" /></div>
              <div class="anim-label">{{ lang === 'zh' ? item.titleZh : item.titleEn }}</div>
              <div class="anim-dl-wrap" @click.stop>
                <button class="anim-dl" @click="dlMenuKey = dlMenuKey === item.index ? null : item.index">{{ lang === 'zh' ? '下载' : 'DOWNLOAD' }} ▾</button>
                <div class="anim-dl-menu" v-if="dlMenuKey === item.index">
                  <button class="anim-dl-opt" @click="openDlDialog(item, 'fbx')">FBX</button>
                  <button class="anim-dl-opt" @click="openDlDialog(item, 'glb')">GLB</button>
                </div>
              </div>
            </li>
          </ul>
        </div>

      </div>

      <!-- RIGHT: 3D viewer -->
      <div class="viewer-panel">
        <div class="viewer-title">{{ displayTitle }}</div>
        <div id="dome" class="viewer-canvas"></div>
      </div>
    </div>

    <footer class="footer">
      <span>{{ lang === 'zh' ? 'DANCELAB 舞蹈动作信息管理系统 · 著作权 2023SR0604211' : 'DANCELAB Dance Motion Management System · Copyright 2023SR0604211' }}</span>
      <span>{{ lang === 'zh' ? '北京市教委 2020 科技计划 · KM202010051001' : 'Beijing Education Commission 2020 Technology Project · KM202010051001' }}</span>
    </footer>

    <!-- Download request dialog -->
    <div class="dl-overlay" v-if="showDlDialog" @click.self="showDlDialog = false">
      <div class="dl-dialog">
        <div class="dl-dialog-header">
          <span class="dl-dialog-title">{{ lang === 'zh' ? '下载申请' : 'DOWNLOAD REQUEST' }}</span>
          <button class="dl-dialog-close" @click="showDlDialog = false">✕</button>
        </div>
        <p class="dl-dialog-desc">{{ lang === 'zh' ? '填写以下信息，我们将在新版本上线时第一时间与您联系' : 'Fill in the form below and we will notify you when a new version is released.' }}</p>
        <div class="dl-field">
          <label class="dl-label">{{ lang === 'zh' ? '姓名' : 'NAME' }}</label>
          <input class="dl-input" v-model="dlForm.name" :placeholder="lang === 'zh' ? '请输入您的姓名' : 'Your name'" />
        </div>
        <div class="dl-field">
          <label class="dl-label">{{ lang === 'zh' ? '专业背景' : 'BACKGROUND' }}</label>
          <textarea class="dl-input dl-textarea" v-model="dlForm.background" :placeholder="lang === 'zh' ? '请介绍您的专业背景，如：舞蹈教师、戏剧导演、艺术设计师…' : 'e.g. Dance teacher, theatre director, art designer…'"></textarea>
        </div>
        <div class="dl-field">
          <label class="dl-label">{{ lang === 'zh' ? '联系邮箱' : 'EMAIL' }}</label>
          <input class="dl-input" type="email" v-model="dlForm.email" :placeholder="lang === 'zh' ? '请输入您的邮箱地址' : 'Your email address'" />
        </div>
        <div class="dl-dialog-actions">
          <button class="dl-btn-cancel" @click="showDlDialog = false">{{ lang === 'zh' ? '取消' : 'CANCEL' }}</button>
          <button class="dl-btn-submit" @click="submitDlDialog">{{ lang === 'zh' ? '确认下载' : 'DOWNLOAD' }}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as THREE from "three";
import Stats from "three/examples/jsm/libs/stats.module";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';

export default {
  name: "vue-three",
  data () {
    return {
      controls: null, container: null, camera: null, scene: null,
      renderer: null, stats: null, mixer: null, clock: null,
      selectedKey: 0,
      folderOpen: true,
      dlMenuKey: null,
      showDlDialog: false,
      pendingDownload: null,
      dlForm: { name: '', background: '', email: '' },
      titleZh: '敬灵',
      titleEn: 'Spirit Offering',
      AnimationList: [
        // ── Part I · 仪式 ──
        { cat: 1, titleZh: '敬灵',            titleEn: 'Spirit Offering',          cover: 'img/jingling04.gif',            animation: 'glb3D/jingling04.glb' },
        { cat: 1, titleZh: '念白',            titleEn: 'Recitation',               cover: 'img/nianbai01.gif',             animation: 'glb3D/nianbai01.glb' },
        // ── Part II · 纯舞短句 ──
        { cat: 2, titleZh: '连接动作与三点鼓1', titleEn: 'Linking Move & 3-Beat I',  cover: 'img/lianjiedongzuo02.gif',      animation: 'glb3D/lianjiedongzuo02.glb' },
        { cat: 2, titleZh: '连接动作与三点鼓2', titleEn: 'Linking Move & 3-Beat II', cover: 'img/lianjiedongzuo03.gif',      animation: 'glb3D/lianjiedongzuo03.glb' },
        { cat: 2, titleZh: '三点鼓蹦子',       titleEn: '3-Beat Drum Jump',         cover: 'img/sandiangu_bengzi02.gif',    animation: 'glb3D/sandiangu_bengzi02.glb' },
        { cat: 2, titleZh: '三点鼓蹲转',       titleEn: '3-Beat Drum Crouch Turn',  cover: 'img/sandiangu_dunzhuan01.gif',  animation: 'glb3D/sandiangu_dunzhuan01.glb' },
        { cat: 2, titleZh: '六点鼓',           titleEn: '6-Beat Drum',              cover: 'img/liudiangu01.gif',           animation: 'glb3D/liudiangu01.glb' },
        { cat: 2, titleZh: '九点鼓冈连',        titleEn: '9-Beat Drum — Ganglian',   cover: 'img/jiudiangu_ganglian01.gif',  animation: 'glb3D/jiudiangu_ganglian01.glb' },
        { cat: 2, titleZh: '九点鼓内外圈',      titleEn: '9-Beat Drum — Inner/Outer',cover: 'img/jiudiangu_neiwaiquan02.gif',animation: 'glb3D/jiudiangu_neiwaiquan02.glb' },
        { cat: 2, titleZh: '九点鼓冈果',        titleEn: '9-Beat Drum — Gangguo',    cover: 'img/jiudiangu_gangguo01.gif',   animation: 'glb3D/jiudiangu_gangguo01.glb' },
        { cat: 2, titleZh: '连三九点鼓',        titleEn: 'Triple 9-Beat Drum',       cover: 'img/liansanjiudiangu01.gif',    animation: 'glb3D/liansanjiudiangu01.glb' },
        // ── Part III · 传统短句 ──
        { cat: 3, titleZh: '古冬仓措',          titleEn: 'Gudong Cangcuo',           cover: 'img/gudongcangcuo01.gif?v=1.1', animation: 'glb3D/gudongcangcuo01.glb' },
        { cat: 3, titleZh: '梅朵啊羌姆',        titleEn: 'Meiduo A-Qiangmu',         cover: 'img/qiangmu01.gif',             animation: 'glb3D/qiangmu01.glb' },
        { cat: 3, titleZh: '松梅宾羌',          titleEn: 'Songmei Binqiang',         cover: 'img/songmeibinqiang01.gif',     animation: 'glb3D/songmeibinqiang01.glb' },
        { cat: 3, titleZh: '铃',               titleEn: 'Bell',                     cover: 'img/ling02.gif',                animation: 'glb3D/ling02.glb' },
      ],
    }
  },
  computed: {
    theme () { return this.$store.state.theme },
    lang ()  { return this.$store.state.lang },
    displayTitle () { return this.lang === 'zh' ? this.titleZh : this.titleEn },
    groupedAnimations () {
      const groups = [
        { roman: 'Ⅰ',  titleZh: '仪式',    titleEn: 'RITUAL',               catKey: 1, items: [] },
        { roman: 'Ⅱ',  titleZh: '纯舞短句', titleEn: 'PURE DANCE PHRASES',   catKey: 2, items: [] },
        { roman: 'Ⅲ',  titleZh: '传统短句', titleEn: 'TRADITIONAL PHRASES',  catKey: 3, items: [] },
      ]
      this.AnimationList.forEach((item, index) => {
        const g = groups.find(g => g.catKey === item.cat)
        if (g) g.items.push({ ...item, index })
      })
      return groups
    },
  },
  watch: {
    theme (val) {
      if (this.renderer) this.renderer.setClearColor(val === 'dark' ? 0x111111 : 0xf0f0f0)
    },
  },
  methods: {
    toggleTheme () { this.$store.commit('toggleTheme') },
    toggleLang ()  { this.$store.commit('toggleLang') },
    selectAnim (item, key) {
      this.selectedKey = key
      this.titleZh = item.titleZh
      this.titleEn = item.titleEn
      this.init(item.animation)
    },
    openDlDialog (item, format) {
      this.pendingDownload = { item, format }
      this.dlMenuKey = null
      this.showDlDialog = true
    },
    async submitDlDialog () {
      const { item, format } = this.pendingDownload
      const base = item.animation.split('/').pop().replace(/\.\w+$/, '')
      try {
        await fetch(window.EMAIL_FUNC_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name:       this.dlForm.name,
            background: this.dlForm.background,
            email:      this.dlForm.email,
            message:    `请求格式：${base}.${format}`,
          }),
        })
      } catch (e) { console.error('提交失败', e) }
      this.showDlDialog = false
      this.dlForm = { name: '', background: '', email: '' }
    },
    clearScene () {
      cancelAnimationFrame(this.animate)
      this.scene.traverse(c => { c = null })
      document.getElementById('dome').innerHTML = ''
      this.renderer.forceContextLoss()
      this.renderer.dispose()
      this.scene.clear()
      this.scene = null; this.camera = null; this.controls = null
      this.renderer.domElement = null; this.renderer = null
    },
    init (path) {
      if (this.scene) this.clearScene()
      this.clock = new THREE.Clock()
      this.container = document.getElementById('dome')
      this.scene = new THREE.Scene()

      const w = this.container.clientWidth || 580
      const h = Math.max(Math.round(w * 0.7), 280)
      this.camera = new THREE.PerspectiveCamera(75, w / h, 0.1, 1000)
      this.camera.position.set(0, 2, 5)
      this.camera.lookAt(0, 0, 0)

      const bgColor = this.theme === 'dark' ? 0x111111 : 0xf0f0f0
      this.renderer = new THREE.WebGLRenderer({ antialias: true })
      this.renderer.setPixelRatio(window.devicePixelRatio)
      this.renderer.setSize(w, h)
      this.renderer.setClearColor(bgColor)
      this.renderer.shadowMap.enabled = true
      this.container.appendChild(this.renderer.domElement)

      const gc = this.theme === 'dark' ? 0x333333 : 0xcccccc
      const grid = new THREE.GridHelper(20, 20, gc, gc)
      grid.material.opacity = 0.4; grid.material.transparent = true
      this.scene.add(grid)
      this.scene.add(new THREE.AmbientLight(0xffffff, 0.4))

      const loader = new GLTFLoader()
      let that = this
      loader.load(path, gltf => {
        that.scene.add(gltf.scene)
        const light = new THREE.PointLight(0xffffff, 4, 0)
        light.position.set(100, 100, 100)
        that.scene.add(light)
        that.mixer = new THREE.AnimationMixer(gltf.scene)
        gltf.animations.forEach(a => that.mixer.clipAction(a).play())
      })

      this.controls = new OrbitControls(this.camera, this.renderer.domElement)
      this.stats = new Stats()
      this._onResize = () => {
        if (!this.renderer || !this.container) return
        const w = this.container.clientWidth
        const h = Math.max(Math.round(w * 0.7), 280)
        this.camera.aspect = w / h
        this.camera.updateProjectionMatrix()
        this.renderer.setSize(w, h)
      }
      window.addEventListener('resize', this._onResize)
      this.$nextTick(() => this.animate())
    },
    animate () {
      const delta = this.clock.getDelta()
      if (this.mixer) this.mixer.update(delta)
      this.renderer.render(this.scene, this.camera)
      this.stats.update()
      requestAnimationFrame(this.animate)
    },
  },
  mounted () {
    this.init('glb3D/jingling04.glb')
    document.title = '藏族热巴舞蹈（男）'
  },
  beforeDestroy () {
    window.removeEventListener('resize', this._onResize)
  },
}
</script>

<style scoped>
.page {
  --black: #000000; --surface: #111111; --surface-raised: #1a1a1a;
  --border: #222222; --border-v: #333333;
  --text-dis: #666666; --text-sec: #999999; --text-pri: #e8e8e8; --text-disp: #ffffff;
  --accent: #d71921; --ease: 200ms cubic-bezier(0.25, 0.1, 0.25, 1);
}
.page.light {
  --black: #f5f5f5; --surface: #ffffff; --surface-raised: #f0f0f0;
  --border: #e8e8e8; --border-v: #cccccc;
  --text-dis: #999999; --text-sec: #666666; --text-pri: #1a1a1a; --text-disp: #000000;
}
* { margin: 0; padding: 0; box-sizing: border-box; }
.page {
  min-height: 100vh; background: var(--black);
  font-family: 'Space Grotesk', system-ui, sans-serif; color: var(--text-pri);
  display: flex; flex-direction: column;
  transition: background var(--ease), color var(--ease);
}

/* ── NAV ── */
.nav {
  height: 48px; background: var(--black); border-bottom: 1px solid var(--border);
  display: flex; align-items: center; padding: 0 32px; flex-shrink: 0;
  transition: background var(--ease), border-color var(--ease);
}
.nav-brand {
  font-family: 'Doto', 'Space Mono', monospace; font-size: 20px;
  color: var(--text-disp); letter-spacing: -0.01em; user-select: none; flex-shrink: 0;
}
.nav-links { display: flex; align-items: center; margin-left: auto; }
.nav-sep { font-family: 'Space Mono', monospace; font-size: 10px; color: var(--border-v); margin: 0 14px; }
.nav-link {
  font-family: 'Space Mono', monospace; font-size: 11px; letter-spacing: 0.08em;
  color: var(--text-dis); text-decoration: none; transition: color var(--ease); white-space: nowrap;
}
.nav-link:hover { color: var(--text-sec); }
.nav-link.is-active { color: var(--text-disp); }
.nav-controls {
  display: flex; align-items: center; gap: 16px;
  margin-left: 24px; padding-left: 24px; border-left: 1px solid var(--border);
}
.ctrl-lang { display: flex; align-items: center; gap: 5px; cursor: pointer; user-select: none; }
.ctrl-lang span, .ctrl-theme {
  font-family: 'Space Mono', monospace; font-size: 11px; letter-spacing: 0.06em;
  color: var(--text-dis); transition: color var(--ease);
}
.ctrl-lang:hover span { color: var(--text-sec); }
.ctrl-lang span.ctrl-active { color: var(--text-disp); }
.ctrl-dot { color: var(--border-v) !important; }
.ctrl-theme { cursor: pointer; user-select: none; font-size: 14px; letter-spacing: 0; color: var(--text-sec); line-height: 1; transition: color var(--ease); }
.ctrl-theme:hover { color: var(--text-disp); }

/* ── CONTENT ── */
.content { flex: 1; display: flex; padding: 24px 32px; gap: 32px; align-items: flex-start; }

/* ── LIST PANEL ── */
.list-panel {
  flex: 1; height: calc(100vh - 48px - 48px - 60px); overflow-y: auto; min-width: 0;
}
.list-panel::-webkit-scrollbar { width: 2px; }
.list-panel::-webkit-scrollbar-thumb { background: var(--border-v); }

/* folder header */
.dance-folder {
  display: flex; align-items: center; gap: 8px;
  padding: 10px 0; margin-bottom: 16px;
  border-bottom: 1px solid var(--border);
  cursor: pointer; user-select: none;
}
.dance-folder:hover .folder-name { opacity: 0.8; }
.folder-slash {
  font-family: 'Space Mono', monospace; font-size: 13px;
  color: var(--accent); line-height: 1;
}
.folder-name {
  font-family: 'Space Mono', monospace; font-size: 11px;
  letter-spacing: 0.06em; color: var(--text-disp);
  transition: opacity var(--ease);
}
.folder-toggle {
  font-family: 'Space Mono', monospace; font-size: 10px;
  color: var(--text-sec); margin-left: auto;
  transition: color var(--ease);
}
.dance-folder:hover .folder-toggle { color: var(--text-disp); }

/* category sections */
.cat-section { margin-bottom: 20px; }
.cat-header {
  display: flex; align-items: center; gap: 10px;
  padding: 6px 0 6px; margin-bottom: 8px;
  border-bottom: 1px solid var(--border);
}
.cat-roman {
  font-family: 'Space Mono', monospace; font-size: 11px; color: var(--accent);
}
.cat-label {
  font-family: 'Space Mono', monospace; font-size: 10px;
  letter-spacing: 0.08em; color: var(--text-sec);
}

/* animation grid */
.anim-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 6px; list-style: none; padding: 0; }
.anim-card {
  background: var(--surface); border: 1px solid var(--border); border-left: 2px solid transparent;
  cursor: pointer; overflow: hidden; transition: border-color var(--ease), background var(--ease);
}
.anim-card:hover { border-color: var(--border-v); border-left-color: var(--border-v); }
.anim-card.selected { border-color: var(--border-v); border-left: 2px solid var(--accent); }
.anim-thumb img {
  width: 100%; height: 110px; object-fit: cover; display: block;
  filter: brightness(0.8); transition: filter var(--ease);
}
.anim-card:hover .anim-thumb img, .anim-card.selected .anim-thumb img { filter: brightness(1); }
.anim-label {
  font-family: 'Space Mono', monospace; font-size: 10px; letter-spacing: 0.05em;
  color: var(--text-sec); padding: 5px 7px; white-space: nowrap; overflow: hidden;
  text-overflow: ellipsis; border-top: 1px solid var(--border); transition: color var(--ease);
}
.anim-card.selected .anim-label { color: var(--text-disp); }
.anim-dl-wrap { position: relative; border-top: 1px solid var(--border); }
.anim-dl {
  display: block; width: 100%; padding: 5px 0;
  background: none; border: none;
  font-family: 'Space Mono', monospace; font-size: 9px; letter-spacing: 0.08em;
  color: var(--text-dis); cursor: pointer; transition: color var(--ease), background var(--ease);
  text-align: center;
}
.anim-dl:hover { color: var(--accent); background: var(--surface-raised); }
.anim-dl-menu {
  position: absolute; bottom: 100%; left: 0; right: 0; z-index: 10;
  background: var(--surface-raised); border: 1px solid var(--border-v);
  display: flex; flex-direction: column;
}
.anim-dl-opt {
  display: block; width: 100%; padding: 5px 0;
  background: none; border: none; border-bottom: 1px solid var(--border);
  font-family: 'Space Mono', monospace; font-size: 9px; letter-spacing: 0.08em;
  color: var(--text-sec); cursor: pointer; transition: color var(--ease), background var(--ease);
  text-align: center;
}
.anim-dl-opt:last-child { border-bottom: none; }
.anim-dl-opt:hover { color: var(--accent); background: var(--surface); }

/* ── VIEWER ── */
.viewer-panel {
  flex-shrink: 0; width: 580px; background: var(--surface); border: 1px solid var(--border);
  overflow: hidden; transition: background var(--ease), border-color var(--ease);
}
.viewer-title {
  height: 40px; display: flex; align-items: center; justify-content: center;
  font-family: 'Space Mono', monospace; font-size: 13px; letter-spacing: 0.08em;
  color: var(--text-disp); border-bottom: 1px solid var(--border); padding: 0 16px;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  transition: color var(--ease), border-color var(--ease);
}
.viewer-canvas { width: 580px; height: 700px; }
canvas { display: block; }

/* ── FOOTER ── */
.footer {
  border-top: 1px solid var(--border); padding: 12px 32px; display: flex;
  gap: 32px; justify-content: center; flex-shrink: 0; transition: border-color var(--ease);
}
.footer span {
  font-family: 'Space Mono', monospace; font-size: 10px; letter-spacing: 0.05em; color: var(--text-dis);
}

/* ── RESPONSIVE ── */
@media only screen and (max-width: 1200px) { .anim-thumb img { height: 90px; } }

@media only screen and (max-width: 900px) {
  .content { flex-direction: column; padding: 16px; gap: 16px; }
  .list-panel { width: 100%; height: auto; max-height: 50vh; }
  .viewer-panel { width: 100%; }
  .viewer-canvas { width: 100%; height: 400px; }
  .footer { flex-direction: column; gap: 4px; align-items: center; }
}

@media only screen and (max-width: 480px) {
  /* nav */
  .nav { padding: 0 12px; gap: 8px; flex-wrap: wrap; height: auto; min-height: 48px; }
  .nav-brand { font-size: 15px; }
  .nav-links { margin-left: 0; width: 100%; justify-content: center; padding: 4px 0; }
  .nav-sep { margin: 0 8px; }
  .nav-link { font-size: 10px; }
  .nav-controls { margin-left: auto; padding-left: 12px; border-left: none; }

  /* content */
  .content { padding: 10px; gap: 10px; }

  /* list */
  .list-panel { max-height: 42vh; }
  .anim-grid { grid-template-columns: repeat(2, 1fr); gap: 6px; }
  .anim-thumb img { height: 80px; }
  .anim-label { font-size: 9px; padding: 4px 6px; }
  .anim-dl { padding: 6px 0; font-size: 9px; }

  /* viewer */
  .viewer-canvas { height: 52vw; min-height: 220px; }

  /* footer */
  .footer { padding: 8px 12px; gap: 4px; }
  .footer span { font-size: 9px; text-align: center; }
}

/* ── DOWNLOAD DIALOG ── */
.dl-overlay {
  position: fixed; inset: 0; z-index: 100;
  background: rgba(0, 0, 0, 0.72);
  display: flex; align-items: center; justify-content: center;
}
.dl-dialog {
  background: var(--surface); border: 1px solid var(--border-v);
  width: 420px; max-width: calc(100vw - 32px);
  padding: 28px 28px 24px;
}
.dl-dialog-header {
  display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px;
}
.dl-dialog-title {
  font-family: 'Space Mono', monospace; font-size: 13px; letter-spacing: 0.08em; color: var(--text-disp);
}
.dl-dialog-close {
  background: none; border: none; font-size: 12px; color: var(--text-dis); cursor: pointer;
  transition: color var(--ease);
}
.dl-dialog-close:hover { color: var(--text-disp); }
.dl-dialog-desc {
  font-family: 'Space Grotesk', sans-serif; font-size: 12px; color: var(--text-sec);
  line-height: 1.6; margin-bottom: 20px; border-left: 2px solid var(--accent); padding-left: 10px;
}
.dl-field { margin-bottom: 16px; }
.dl-label {
  display: block; font-family: 'Space Mono', monospace; font-size: 10px;
  letter-spacing: 0.08em; color: var(--text-sec); margin-bottom: 6px;
}
.dl-input {
  width: 100%; background: var(--surface-raised); border: 1px solid var(--border);
  color: var(--text-pri); font-family: 'Space Grotesk', sans-serif; font-size: 13px;
  padding: 8px 10px; outline: none; transition: border-color var(--ease);
  resize: none;
}
.dl-input:focus { border-color: var(--border-v); }
.dl-textarea { height: 72px; }
.dl-dialog-actions {
  display: flex; gap: 8px; justify-content: flex-end; margin-top: 20px;
}
.dl-btn-cancel {
  background: none; border: 1px solid var(--border); color: var(--text-dis);
  font-family: 'Space Mono', monospace; font-size: 10px; letter-spacing: 0.08em;
  padding: 7px 16px; cursor: pointer; transition: border-color var(--ease), color var(--ease);
}
.dl-btn-cancel:hover { border-color: var(--border-v); color: var(--text-sec); }
.dl-btn-submit {
  background: var(--accent); border: 1px solid var(--accent); color: #fff;
  font-family: 'Space Mono', monospace; font-size: 10px; letter-spacing: 0.08em;
  padding: 7px 20px; cursor: pointer; transition: opacity var(--ease);
}
.dl-btn-submit:hover { opacity: 0.85; }
</style>
